import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { STUDIO_SEARCH_DEFAULT_SORT_BY } from 'constants/app';
import { FilterAttributeGroup } from 'store/category/types';
import updateProperty from 'utils/store/updateProperty';
import setStudioSearchFiltersSelectionReducer from './reducer/setStudioSearchFiltersSelectionReducer';
import setStudioSearchResultsReducer from './reducer/setStudioSearchResultsReducer';
import { StudioSearchState } from './types';

export const initialState: StudioSearchState = {
  isLoading: true,
  query: '',
  categoryFilter: null,
  searchResults: [],
  recentlyViewed: [],
  filters: undefined,
  pageNr: 0,
  minPage: 99999,
  maxPage: 0,
  totalResultsAvailable: 0,
  sortBy: STUDIO_SEARCH_DEFAULT_SORT_BY,
  gettyAbstractProduct: undefined,
  refetchFlag: true,
};

const { reducer, actions } = createSlice({
  name: 'category',
  initialState,
  reducers: {
    setStudioSearchLoadingAction: updateProperty('isLoading'),
    setStudioSearchFiltersAction: updateProperty('filters'),
    setStudioSearchSortByAction: updateProperty('sortBy'),
    setStudioSearchPageNrAction: updateProperty('pageNr', (pageNr) => pageNr || 1),
    setStudioSearchQueryAction: updateProperty('query'),
    setStudioSearchCategoryAction: updateProperty('searchCategory'),
    setStudioSearchRefetchFlagAction: updateProperty('refetchFlag'),
    setGettyAbstractProductAction: updateProperty('gettyAbstractProduct'),
    setRecentlyViewedAction: updateProperty('recentlyViewed'),
    setStudioSearchCategoryFiltersAction: (state, { payload }: PayloadAction<string>) => {
      const filters = JSON.parse(payload) as { name: string; values: string[] }[];
      state.categoryFilter = {
        attributeFilters: [],
        tagFilters: filters.map((filter) => ({
          name: filter.name,
          typeName: filter.name,
          optionTemplate: '',
          options: filter.values.map((value, order) => ({
            name: filter.name,
            value,
            order,
            isSelected: true,
            isAvailable: true,
          })),
        })),
      };
    },
    resetStudioSearchResultsAction: (state) => {
      state.searchResults = [];
      state.totalResultsAvailable = 0;
      state.minPage = 99999;
      state.maxPage = 0;
    },
    updateFilterAttributeOptionAction: (
      state,
      {
        payload,
      }: PayloadAction<{
        typeName: string;
        value: string;
        group: FilterAttributeGroup;
      }>,
    ) => {
      const targetAttribute = state.filters?.[payload.group]?.find(({ typeName }) => typeName === payload.typeName);
      const targetOption = targetAttribute?.options?.find(({ value }) => value === payload.value);
      if (targetOption) {
        targetOption.isSelected = !targetOption.isSelected;
      }
    },
    resetStudioSearchFiltersAction: (state) => {
      state.filters = undefined;
    },
    setStudioSearchFiltersSelectionAction: setStudioSearchFiltersSelectionReducer,
    setStudioSearchResultsAction: setStudioSearchResultsReducer,
  },
});

export const {
  resetStudioSearchFiltersAction,
  resetStudioSearchResultsAction,
  setGettyAbstractProductAction,
  setRecentlyViewedAction,
  setStudioSearchCategoryAction,
  setStudioSearchFiltersSelectionAction,
  setStudioSearchLoadingAction,
  setStudioSearchPageNrAction,
  setStudioSearchQueryAction,
  setStudioSearchRefetchFlagAction,
  setStudioSearchResultsAction,
  setStudioSearchSortByAction,
  updateFilterAttributeOptionAction,
  setStudioSearchCategoryFiltersAction,
} = actions;

export default reducer;
